'use client';

/**
 * useYouTubeDuration — fetches a YouTube video's runtime for the voice
 * pinned-card slot's duration badge (design turn 11 part B).
 *
 * Reuses lib/youtube's `getYouTubeId` extractor (the same one
 * VideoPlayer/RagAnswerBlock/ThinkingSteps use) rather than re-parsing the
 * URL. Returns null — never a duration string, and NEVER the literal
 * "0:00" — whenever there is nothing trustworthy to show:
 *   - `url` isn't a YouTube URL (no videoId)
 *   - `NEXT_PUBLIC_YOUTUBE_API_KEY` isn't configured
 *   - the fetch fails, or the API returns no matching video
 *   - the parsed duration is non-positive
 *
 * Caches by videoId at module scope (survives remounts within the session) —
 * INCLUDING null results, so a failed/unavailable lookup is never retried.
 * Aborts the in-flight fetch on unmount or a videoId change.
 */

import { useEffect, useState } from 'react';
import { getYouTubeId } from '@/lib/youtube';

const durationCache = new Map<string, string | null>();

/** ISO-8601 duration (PT#H#M#S, every part optional) → total seconds, or
 *  null if the string doesn't match the expected shape or is empty
 *  (e.g. bare "PT" — YouTube never sends this, but don't trust it blindly). */
function parseIso8601Duration(duration: string): number | null {
  const match = duration.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!match || (!match[1] && !match[2] && !match[3])) return null;
  const hours = match[1] ? parseInt(match[1], 10) : 0;
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const seconds = match[3] ? parseInt(match[3], 10) : 0;
  return hours * 3600 + minutes * 60 + seconds;
}

/** total seconds → "M:SS" (under an hour) or "H:MM:SS". Non-positive input
 *  is treated as "unknown" (null) rather than ever rendering "0:00". */
function formatDuration(totalSeconds: number): string | null {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return null;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function useYouTubeDuration(
  url: string | null | undefined
): string | null {
  const videoId = url ? getYouTubeId(url) : null;
  const [duration, setDuration] = useState<string | null>(
    videoId ? (durationCache.get(videoId) ?? null) : null
  );

  useEffect(() => {
    if (!videoId) {
      setDuration(null);
      return;
    }
    if (durationCache.has(videoId)) {
      setDuration(durationCache.get(videoId) ?? null);
      return;
    }
    const apiKey = process.env.NEXT_PUBLIC_YOUTUBE_API_KEY;
    if (!apiKey) {
      // Not cached — no fetch happened, so nothing to remember; a later env
      // change (unlikely mid-session, but harmless either way) just re-tries.
      setDuration(null);
      return;
    }

    const controller = new AbortController();
    setDuration(null);

    fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${videoId}&key=${apiKey}`,
      { signal: controller.signal }
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        const iso = data?.items?.[0]?.contentDetails?.duration;
        const totalSeconds =
          typeof iso === 'string' ? parseIso8601Duration(iso) : null;
        const formatted =
          totalSeconds != null ? formatDuration(totalSeconds) : null;
        durationCache.set(videoId, formatted);
        setDuration(formatted);
      })
      .catch((err) => {
        // An aborted fetch (unmount / videoId changed mid-flight) isn't a
        // real failure — the component that cares is already gone/updated.
        if (controller.signal.aborted) return;
        console.warn('[useYouTubeDuration] fetch failed', err);
        durationCache.set(videoId, null);
        setDuration(null);
      });

    return () => controller.abort();
  }, [videoId]);

  return duration;
}
